import {
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { notifyMutationError } from "@/lib/repository-utils";
import { MovieEditFormType } from "@/pages/movies/schemas/movie.edit.form";
import { MovieCastNestedType } from "@/pages/movies/schemas/movie.nested.form";

export interface MovieSummary {
  id: string;
  name: string;
  posterUrl?: string;
  releaseYear?: string;
  director?: string;
}

export const useMovieRepository = () => {
  const { getList, getOne } = useDataProvider()();
  const { mutateAsync: updateAsync } = useUpdate({ resource: "movies" });
  const { edit } = useNavigation();
  const { open } = useNotification();

  async function search(query: string): Promise<MovieSummary[]> {
    try {
      if (!query) return [];
      // Same Refine getList -> /movies/explore path the resource list pages and the
      // venue/artist search use (proven). A raw `custom` GET diverges from it
      // (trailing-slash URL + hand-built params) and fails against the explore endpoint.
      const response = await getList({
        resource: "movies",
        pagination: { currentPage: 0, pageSize: 25 },
        filters: [],
        meta: {
          sortBy: "_text_match:desc",
          query,
          // Movies have no `name` field in the Typesense schema (they use title /
          // originalTitle, like plays); querying `name` returns a 400.
          queryBy: "title,originalTitle",
          // The explore endpoint's default projection omits `cast`, so we ask for
          // exactly the fields the result rows render: title/year, poster (media),
          // and director (cast). includeFields replaces the default entirely.
          includeFields: [
            "id",
            "title",
            "originalTitle",
            "releaseDate",
            "media",
            "cast",
          ],
        },
      });
      return response.data.map((i: any) => ({
        id: i?.document?.id,
        name: i?.document?.name ?? i?.document?.title,
        posterUrl: i?.document?.media?.photos?.[0]?.url,
        releaseYear: releaseYearOf(i?.document),
        director: directorOf(i?.document),
      }));
    } catch (ex) {
      console.error("Movie search failed due to:", ex);
      open?.({
        message: `Movie search failed due to ${toHumanErrorMessage(ex)}`,
        type: "error",
      });
      return [];
    }
  }

  async function get(id: string): Promise<any> {
    return getOne({ id, resource: "movies" });
  }

  /**
   * Partial update. The form holds a Movie-shaped object, but the backend
   * `UpdateMovieCommand` wants `genreIds` (not genre objects) and cast entries
   * reduced to personId|name. We translate any changed fields here.
   */
  async function update(values: Partial<MovieEditFormType>) {
    const payload: Record<string, unknown> = { id: values.id };

    for (const [key, value] of Object.entries(values)) {
      if (key === "id" || key === "genres" || key === "cast") continue;
      payload[key] = value;
    }
    if (values.genres !== undefined) {
      payload.genreIds = (values.genres ?? []).map((g) => g.id);
    }
    if (values.cast !== undefined) {
      payload.cast = (values.cast ?? []).map(toCastRequest);
    }

    const response = await updateAsync?.(
      { id: values.id!, values: payload },
      {
        onSuccess: (data) => {
          console.debug("Successful movie update", data);
          edit("movies", values.id!, "replace");
          open?.({
            type: "success",
            message: "Successfully updated movie",
            description: `You updated ${values.title ?? "a movie"}`,
          });
        },
        onError: (error) => {
          console.error("Failed to update movie with values", values, error);
          notifyMutationError(open, "Update movie request", error);
        },
      },
    );

    return response?.data.id?.toString();
  }

  return { search, get, update };
};

/**
 * Reduce a form cast entry to the `MovieCastRequestDto` the backend expects:
 * exactly one of personId | name, plus characterName/department. Linked members
 * keep only personId (the server re-derives name/photo); unknown members send
 * only their free-text name.
 */
function toCastRequest(c: MovieCastNestedType) {
  const linked = !!c.personId;
  return {
    personId: linked ? c.personId : null,
    name: linked ? null : (c.name ?? ""),
    characterName: c.characterName ?? null,
    department: c.department || "Acting",
  };
}

/**
 * Director(s) from a movie's cast/crew: the names of entries whose department is
 * "Directing" (the value the movie cast editor writes), joined for the occasional
 * co-directed film. `undefined` when there is no directing credit.
 */
function directorOf(doc: any): string | undefined {
  const names = (doc?.cast ?? [])
    .filter((c: any) => c?.department === "Directing")
    .map((c: any) => c?.name)
    .filter(Boolean);
  return names.length ? names.join(", ") : undefined;
}

function releaseYearOf(doc: any): string | undefined {
  const raw = doc?.releaseDate ?? doc?.release_date ?? doc?.releaseYear;
  if (typeof raw === "string" && raw.length >= 4) return raw.slice(0, 4);
  if (typeof raw === "number") {
    // Typesense stores release dates as unix seconds; fall back to a bare year.
    const year =
      raw > 9999 ? new Date(raw * 1000).getUTCFullYear() : Math.trunc(raw);
    return Number.isFinite(year) ? String(year) : undefined;
  }
  return undefined;
}
